import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Pencil, Save, Wrench, Sparkles, ShieldCheck, Cpu, Package, Truck, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, resolveImg } from "@/lib/api";
import ImageUpload from "@/components/admin/ImageUpload";
import RichTextEditor from "@/components/admin/RichTextEditor";

const ICON_OPTIONS = ["Wrench", "Sparkles", "ShieldCheck", "Cpu", "Package", "Truck", "Settings"];
const ICON_MAP = { Wrench, Sparkles, ShieldCheck, Cpu, Package, Truck, Settings };

const empty = {
  id: "",
  title: "",
  description: "",
  badge: "",
  icon: "Wrench",
  image_url: "",
  order: 99,
  available: true,
  slug: "",
  content: "",
  seo_title: "",
  seo_description: "",
  faq: [],
};

export default function ServicesPanel() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/services");
      setServices(data);
    } catch {
      toast.error("Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({ ...empty, id: crypto.randomUUID(), order: services.length + 1, _new: true });
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing({ ...s });
    setOpen(true);
  };

  const save = async () => {
    if (!editing.title) {
      toast.error("Titlul este obligatoriu");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload._new;
      if (editing._new) {
        await api.post("/admin/services", payload);
      } else {
        await api.put(`/admin/services/${editing.id}`, payload);
      }
      toast.success("Serviciu salvat");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/services/${id}`);
    setServices((s) => s.filter((x) => x.id !== id));
    toast.success("Serviciu șters");
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-slate-500">
          {services.length} servicii afișate pe site
        </p>
        <Button
          onClick={openNew}
          data-testid="admin-service-add-button"
          className="bg-[#FF5A1F] hover:bg-[#E04B14] text-white font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" /> Adaugă serviciu
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.map((s) => (
          <div
            key={s.id}
            className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex gap-4"
          >
            <div className="w-16 h-16 rounded-lg bg-[#F4F6F9] overflow-hidden shrink-0 flex items-center justify-center">
              {s.image_url ? (
                <img src={resolveImg(s.image_url)} alt="" className="w-full h-full object-cover" />
              ) : (
                (() => {
                  const Ic = ICON_MAP[s.icon] || Wrench;
                  return <Ic className="w-6 h-6 text-[#0F1E36]" />;
                })()
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-[#0F1E36] truncate">{s.title}</h3>
                {!s.available && (
                  <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                    ascuns
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 line-clamp-2 mt-1">{s.description}</p>
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(s)}
                  className="border-[#E2E8F0] h-8"
                >
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editează
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#FEE2E2] text-[#DC2626] hover:bg-[#FEE2E2] h-8"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Ștergi serviciul „{s.title}"?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Serviciul va fi eliminat de pe site.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Anulează</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => remove(s.id)}
                        className="bg-[#DC2626] hover:bg-[#B91C1C]"
                      >
                        Șterge
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing?._new ? "Adaugă serviciu" : "Editează serviciu"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Titlu *</Label>
                <Input
                  data-testid="admin-service-title-input"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="font-semibold">Slug (URL pagină)</Label>
                <Input
                  data-testid="admin-service-slug-input"
                  value={editing.slug || ""}
                  onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                  placeholder="ex: mecanica-auto (gol = generat din titlu)"
                  className="mt-1.5"
                />
                <p className="text-xs text-slate-400 mt-1">/servicii/{editing.slug || "..."}</p>
              </div>
              <div>
                <Label className="font-semibold">Descriere scurtă (card / teaser)</Label>
                <Textarea
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={3}
                  className="mt-1.5"
                />
                <p className="text-xs text-slate-400 mt-1">Apare pe cardul din pagina principală și ca rezumat.</p>
              </div>
              <div>
                <Label className="font-semibold">Conținut pagină serviciu (SEO)</Label>
                <div className="mt-1.5">
                  <RichTextEditor
                    value={editing.content || ""}
                    onChange={(v) => setEditing({ ...editing, content: v })}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">Textul complet afișat pe pagina dedicată /servicii/...</p>
              </div>
              <div>
                <Label className="font-semibold">Întrebări frecvente (FAQ)</Label>
                <div className="mt-1.5 space-y-3">
                  {(editing.faq || []).map((f, i) => (
                    <div key={i} className="border border-[#E2E8F0] rounded-lg p-3 space-y-2" data-testid={`admin-faq-item-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-500">Întrebarea {i + 1}</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 text-red-500 hover:text-red-600"
                          data-testid={`admin-faq-remove-${i}`}
                          onClick={() => setEditing({ ...editing, faq: (editing.faq || []).filter((_, j) => j !== i) })}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <Input
                        placeholder="Întrebare"
                        value={f.q || ""}
                        onChange={(e) => {
                          const n = [...(editing.faq || [])];
                          n[i] = { ...n[i], q: e.target.value };
                          setEditing({ ...editing, faq: n });
                        }}
                      />
                      <Textarea
                        rows={2}
                        placeholder="Răspuns"
                        value={f.a || ""}
                        onChange={(e) => {
                          const n = [...(editing.faq || [])];
                          n[i] = { ...n[i], a: e.target.value };
                          setEditing({ ...editing, faq: n });
                        }}
                      />
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    className="border-[#E2E8F0] w-full"
                    data-testid="admin-faq-add-button"
                    onClick={() => setEditing({ ...editing, faq: [...(editing.faq || []), { q: "", a: "" }] })}
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> Adaugă întrebare
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Etichetă (badge)</Label>
                  <Input
                    value={editing.badge}
                    onChange={(e) => setEditing({ ...editing, badge: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="font-semibold">Ordine</Label>
                  <Input
                    type="number"
                    value={editing.order}
                    onChange={(e) =>
                      setEditing({ ...editing, order: parseInt(e.target.value) || 0 })
                    }
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div>
                <Label className="font-semibold">Pictogramă</Label>
                <select
                  value={editing.icon}
                  onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                  className="mt-1.5 w-full h-10 rounded-md border border-[#E2E8F0] bg-white px-3 text-sm"
                >
                  {ICON_OPTIONS.map((ic) => (
                    <option key={ic} value={ic}>
                      {ic}
                    </option>
                  ))}
                </select>
              </div>
              <ImageUpload
                label="Imagine serviciu (opțional)"
                value={editing.image_url}
                onChange={(url) => setEditing({ ...editing, image_url: url })}
              />
              <div>
                <Label className="font-semibold">Titlu SEO (opțional)</Label>
                <Input
                  data-testid="admin-service-seo-title-input"
                  value={editing.seo_title || ""}
                  onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })}
                  placeholder="Gol = generat automat din titlu"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="font-semibold">Meta descriere SEO (opțional)</Label>
                <Textarea
                  data-testid="admin-service-seo-desc-input"
                  value={editing.seo_description || ""}
                  onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })}
                  rows={2}
                  placeholder="Gol = generat automat din descriere"
                  className="mt-1.5"
                />
              </div>
              <div className="flex items-center justify-between bg-[#F4F6F9] rounded-lg px-4 py-3">
                <Label className="font-semibold">Afișat pe site</Label>
                <Switch
                  checked={editing.available}
                  onCheckedChange={(v) => setEditing({ ...editing, available: v })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#E2E8F0]">
              Anulează
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="admin-service-save-button"
              className="bg-[#FF5A1F] hover:bg-[#E04B14] text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Salvează</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
